/**
 * IntegrationRow — the SINGLE source of truth for one integration row, shared by
 * BOTH the marketplace deploy modal and the deployed-agent settings list
 * (IntegrationsConnectList) so the two always look + behave identically.
 *
 * Visual contract:
 *   - Name area carries a small inline qualifier (like the "optional" tag):
 *       · required  → a lock icon (this agent depends on it; can't be turned off)
 *       · connected → the word "connected" (it's wired up account-wide)
 *       · optional  → the word "optional" (an optional integration, not connected)
 *   - Right side:
 *       · connected → the word "Connected" — GREEN when this row is selected to
 *         deploy/use for this agent, GREY when connected but not selected.
 *         (Read-only mode is always green = "connected".)
 *       · not connected → a "Connect" button.
 *   - selectable + connected + not-required → the whole row toggles on click.
 *   - A selected row also highlights its whole box green (.is-selected).
 *
 * Props:
 *   id, name, connectPath  — provider identity + where "Connect" goes.
 *   connected   — is the integration connected for this account/workflow.
 *   selectable  — selection (allowlist) mode. false → read-only (no toggle).
 *   selected    — is it enabled for THIS agent (drives green vs grey text + box).
 *   locked      — required: always-on, not toggleable (shows the lock).
 *   optional    — show the "optional" tag when NOT connected.
 *   highlightMissing — deploy-gate marking (modal only): the user clicked
 *                 Deploy with this REQUIRED integration unconnected → steady
 *                 red border (no animation) so the eye lands on what to fix.
 *   onToggle(id), onConnect(id, connectPath) — handlers.
 */

import React from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { lockClosedOutline } from 'ionicons/icons';
import IntegrationLogo from '../IntegrationLogo';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import './IntegrationRow.css';

const IntegrationRow = ({
  id,
  name,
  connectPath = '/integrations',
  connected = false,
  selectable = false,
  selected = false,
  locked = false,
  optional = false,
  highlightMissing = false,
  // busy — this row's allowlist change is being saved. Shows a spinner in place
  // of the tick and makes the row non-interactive, so a rapid clicker can't fire
  // ten toggles a second; the click is a no-op until the save resolves.
  busy = false,
  // No-connection toggleable skill (e.g. codebase-memory): a skill that needs NO
  // integration/token. Renders as a toggle-ONLY row — there is no "Connect"
  // button and no "Connected" account state; it's always a candidate, the row
  // just turns it on/off for THIS agent (reusing the SAME enabledIntegrations
  // allowlist). `description` shows as a small sub-line. Only meaningful in
  // selectable mode (the read-only trigger modal never sets selectable).
  noIntegration = false,
  description = '',
  onToggle,
  onConnect,
}) => {
  // ── No-connection toggleable skill row (toggle-only; no Connect / Connected) ──
  if (noIntegration) {
    // A REQUIRED store skill (dataset-store / gbrain) is locked ON: forced On,
    // non-interactive, with the SAME lock badge as a required integration.
    const isOn = locked ? true : (!selectable || selected); // read-only renders as "on"
    const right = busy ? (
      <>
        <span className="mkt-modal-integration-connected-label mkt-modal-integration-connected-label--off">Saving…</span>
        <IonSpinner name="dots" className="mkt-modal-integration-saving-spinner" aria-label="Saving" />
      </>
    ) : (
      <span
        className={`mkt-modal-integration-connected-label${isOn ? '' : ' mkt-modal-integration-connected-label--off'}`}
        aria-label={isOn ? 'Enabled for this agent' : 'Disabled for this agent'}
      >
        {isOn ? 'On' : 'Off'}
      </span>
    );
    // Description is NOT shown inline (keeps the row compact like the other
    // skill rows). It surfaces as a real hover tooltip via the <Tooltip>
    // component — the native `title` attribute is unreliable (long delay,
    // often suppressed) so we render a proper portal tooltip instead.
    const brandInner = (
      <span className="mkt-modal-integration-brand">
        <IntegrationLogo id={id} size={18} title={name} />
        <span className="mkt-modal-integration-name">{name}</span>
        {locked && (
          <IonIcon icon={lockClosedOutline} className="mkt-modal-integration-name-lock" aria-label="Required" />
        )}
      </span>
    );
    const brandWithDesc = description ? (
      <Tooltip
        position="top"
        content={
          <span style={{ display: 'block', maxWidth: 260, whiteSpace: 'normal', lineHeight: 1.4 }}>
            {description}
          </span>
        }
      >
        {brandInner}
      </Tooltip>
    ) : brandInner;
    // Read-only, OR a REQUIRED store skill → non-interactive row (locked ON).
    if (!selectable || locked) {
      const roCls = `mkt-modal-integration-row is-connected${
        locked ? ' is-selected is-locked' : ''
      }`;
      return (
        <li
          className={roCls}
          aria-disabled={locked || undefined}
          title={locked ? 'Required — always enabled for this agent' : undefined}
        >
          {brandWithDesc}
          <span className="mkt-modal-integration-state">{right}</span>
        </li>
      );
    }
    const cls = [
      'mkt-modal-integration-row',
      'is-connected',
      selected ? 'is-selected' : 'is-deselected',
      busy ? 'is-saving' : 'is-togglable',
    ].join(' ');
    return (
      <li
        className={cls}
        role="button"
        tabIndex={busy ? -1 : 0}
        aria-pressed={selected}
        aria-busy={busy || undefined}
        onClick={() => !busy && onToggle?.(id)}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(id); }
        }}
      >
        {brandWithDesc}
        <span className="mkt-modal-integration-state">{right}</span>
      </li>
    );
  }

  // Inline name qualifier: lock for required, "optional" for optional (shown
  // whether or not it's connected). "connected" is NOT here — it lives on the
  // RIGHT, in the same slot as the "Connect" button, for consistency.
  let nameTag = null;
  if (locked) {
    nameTag = (
      <IonIcon icon={lockClosedOutline} className="mkt-modal-integration-name-lock" aria-label="Required" />
    );
  } else if (optional) {
    nameTag = <span className="mkt-modal-integration-optional-tag">optional</span>;
  }

  const brand = (
    <span className="mkt-modal-integration-brand">
      <IntegrationLogo id={id} size={18} title={name} />
      <span className="mkt-modal-integration-name">{name}</span>
      {nameTag}
    </span>
  );

  // CONNECTED → the word "Connected", green when selected (read-only counts as
  // selected = "connected"), grey when connected-but-not-selected-for-this-agent.
  if (connected) {
    const isOn = !selectable || selected || locked;
    // Right side mirrors the "Connect" slot: the word "Connected" — GREEN when
    // used by this agent, GREY when connected-but-not-used.
    const right = busy ? (
      <>
        <span className="mkt-modal-integration-connected-label mkt-modal-integration-connected-label--off">Saving…</span>
        <IonSpinner name="dots" className="mkt-modal-integration-saving-spinner" aria-label="Saving" />
      </>
    ) : (
      <span
        className={`mkt-modal-integration-connected-label${isOn ? '' : ' mkt-modal-integration-connected-label--off'}`}
        aria-label={isOn ? 'Enabled for this agent' : 'Connected — not used by this agent'}
      >
        Connected
      </span>
    );
    if (!selectable) {
      return (
        <li className="mkt-modal-integration-row is-connected">
          {brand}
          <span className="mkt-modal-integration-state">{right}</span>
        </li>
      );
    }
    // Locked OR mid-save → not interactive. busy shows a spinner + blocks the
    // click so a rapid clicker can't queue ten toggles before the first saves.
    const inert = locked || busy;
    const cls = [
      'mkt-modal-integration-row',
      'is-connected',
      selected || locked ? 'is-selected' : 'is-deselected',
      busy ? 'is-saving' : (locked ? 'is-locked' : 'is-togglable'),
    ].join(' ');
    return (
      <li
        className={cls}
        role="button"
        tabIndex={inert ? -1 : 0}
        aria-pressed={selected || locked}
        aria-disabled={locked || undefined}
        aria-busy={busy || undefined}
        title={locked
          ? 'Required — always enabled for this agent'
          : busy
            ? 'Saving…'
            : selected
              ? 'Enabled for this agent — click to turn off'
              : 'Click to let this agent use it'}
        onClick={() => !inert && onToggle?.(id)}
        onKeyDown={(e) => {
          if (inert) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(id); }
        }}
      >
        {brand}
        <span className="mkt-modal-integration-state">{right}</span>
      </li>
    );
  }

  // NOT CONNECTED → Connect button (+ deploy-gate marking on the modal).
  const rowCls = `mkt-modal-integration-row ${optional ? 'is-optional' : 'is-missing'}${highlightMissing ? ' is-required-missing' : ''}`;
  return (
    <li className={rowCls}>
      {brand}
      <span className="mkt-modal-integration-state">
        <button
          type="button"
          className={optional
            ? 'mkt-modal-integration-connect mkt-modal-integration-connect--optional'
            : 'mkt-modal-integration-connect'}
          onClick={(e) => { e.stopPropagation(); onConnect?.(id, connectPath); }}
        >
          Connect
        </button>
      </span>
    </li>
  );
};

export default IntegrationRow;
